import {
	ActivityIdRouteSync,
	OverlayProvider,
	TenantBrandingProvider,
	ThemeProvider,
	TranslationProvider,
	type TenantBrandingValue,
} from '@supply-mover-2-0/web-ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { JSX } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { BrandThemeApplier } from './components/BrandThemeApplier';
import { HelpChatWidget } from './components/HelpChatWidget';
import { LoginModal } from './components/LoginModal';
import { NativeSplashController } from './components/NativeSplashController';
import { PortalAttributionHydrator } from './components/PortalAttributionHydrator';
import { ScrollToTop } from './components/ScrollToTop';
import { TenantGate } from './components/TenantGate';
import { PortalLayout } from './components/layout/PortalLayout';
import { useDisplaySettings } from './hooks/useDisplaySettings';
import { AccountPage } from './pages/AccountPage';
import { CartPage } from './pages/CartPage';
import { CatalogPage } from './pages/CatalogPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { ClientSelectorPage } from './pages/ClientSelectorPage';
import { DashboardPage } from './pages/DashboardPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { HomePage } from './pages/HomePage';
import { InvoiceDetailPage } from './pages/InvoiceDetailPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { LineReceiptPage } from './pages/LineReceiptPage';
import { MakePaymentPage } from './pages/MakePaymentPage';
import { OrderDetailPage } from './pages/OrderDetailPage';
import { OrdersPage } from './pages/OrdersPage';
import { PaymentMethodsPage } from './pages/PaymentMethodsPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { PromoDetailPage } from './pages/PromoDetailPage';
import { RegisterPage } from './pages/RegisterPage';
import { SetPasswordPage } from './pages/SetPasswordPage';
import { StatementDetailPage } from './pages/StatementDetailPage';
import { StatementsPage } from './pages/StatementsPage';
import { TransactionsPage } from './pages/TransactionsPage';
import {
	getPreferredLanguage,
	getTranslations,
} from './services/portalLanguageService';
import { useLoginModalStore } from './stores/loginModalStore';

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 30000,
			refetchOnWindowFocus: false,
		},
	},
});

/**
 * Build-time brand default for the "Powered by Workd" attribution footer
 * (PD-14113). The storefront is multi-tenant (resolved by hostname), so unlike
 * the brand-built CRM there is no per-brand build — the default is always the
 * Workd platform credit. The backend bootstrap may override this per
 * tenant/storefront (applied by <PortalAttributionHydrator />). Logo URLs match
 * the Workd brand JSON (apps/crm-web/brands/workd.json -> defaultAttribution).
 */
const WORKD_BRAND_DEFAULT: TenantBrandingValue = {
	brandAlias: 'workd',
	brandDisplayName: 'Workd',
	attribution: {
		show: true,
		label: 'Workd',
		logoUrl:
			'https://cdn.titanworkflow.com/titanworkflow/assets/img/workd-email-logo.png',
		logoUrlDark:
			'https://cdn.titanworkflow.com/titanworkflow/assets/img/workd_logo_white.png',
		linkUrl: 'https://workd.com',
	},
};

/**
 * Connects the global login modal store to the LoginModal component.
 * Rendered inside TenantGate so it has access to the tenant context.
 */
function GlobalLoginModal(): JSX.Element {
	const { isOpen, message, close } = useLoginModalStore();
	return <LoginModal isOpen={isOpen} onClose={close} message={message} />;
}

/**
 * Route guard for catalog/browse surfaces. Account-portal-only storefronts
 * (allowCatalogBrowsing off) have no catalog, so a direct URL to /catalog or
 * a promo deep-link redirects to the dashboard rather than rendering a page
 * whose API calls now 403. The nav already hides these entry points; this is
 * the backstop for hand-typed URLs and stale bookmarks.
 */
function RequireCatalogBrowsing({
	children,
}: {
	children: JSX.Element;
}): JSX.Element {
	const display = useDisplaySettings();
	return display.canBrowseCatalog ? (
		children
	) : (
		<Navigate to="/dashboard" replace />
	);
}

/**
 * Route guard for the cart. Browse-only and account-portal-only storefronts
 * have purchasing disabled, so /cart redirects to the dashboard.
 */
function RequirePurchasing({
	children,
}: {
	children: JSX.Element;
}): JSX.Element {
	const display = useDisplaySettings();
	return display.canPurchase ? children : <Navigate to="/dashboard" replace />;
}

/**
 * Route guard for the invoices / statements / transactions surfaces. These
 * sections are hidden entirely for hide-pricing portal users (getMe forces
 * canViewInvoices=false) as well as for storefronts whose invoices section
 * is disabled. The nav already hides these entry points; this is the backstop
 * for hand-typed URLs and stale bookmarks.
 */
function RequireInvoices({ children }: { children: JSX.Element }): JSX.Element {
	const display = useDisplaySettings();
	// PD-14239: block invoices/statements/transactions for hide-pricing users.
	return display.canViewInvoices ? (
		children
	) : (
		<Navigate to="/dashboard" replace />
	);
}

/**
 * Route guard for the payment surfaces (make a payment, pay accounts, manage
 * saved payment methods). Disabled for hide-pricing portal users (getMe forces
 * canPayInvoices=false) and for storefronts without payments enabled. The nav
 * and pay actions already hide these entry points; this is the backstop for
 * hand-typed URLs and stale bookmarks.
 */
function RequirePayments({ children }: { children: JSX.Element }): JSX.Element {
	const display = useDisplaySettings();
	// PD-14239: block payment surfaces for hide-pricing users.
	return display.canPayInvoices ? (
		children
	) : (
		<Navigate to="/dashboard" replace />
	);
}

function App() {
	return (
		<QueryClientProvider client={queryClient}>
			<BrowserRouter>
				<ActivityIdRouteSync />
				{/* Reset window scroll on push/replace navigations (PD-14672) —
				    the storefront scrolls the window, so without this a detail
				    page opened from a scrolled list starts mid-scroll. */}
				<ScrollToTop />
				<ThemeProvider initialTheme="light">
					<TranslationProvider
						fetchTranslations={getTranslations}
						fetchPreferredLanguage={getPreferredLanguage}
					>
						<OverlayProvider>
							<TenantBrandingProvider initialValue={WORKD_BRAND_DEFAULT}>
								{/* Sibling of TenantGate (not a child) so the native
								    splash hides even when the gate resolves to the
								    404 page (PD-14693). */}
								<NativeSplashController />
								<TenantGate>
									{/* Side-effect component: applies brand colors,
								    favicon, and meta theme-color from the
								    portal_site bootstrap response. Renders nothing. */}
									<BrandThemeApplier />
									{/* Side-effect: applies the resolved "Powered by X"
									    attribution override from the bootstrap onto the
									    TenantBrandingProvider context (PD-14113). */}
									<PortalAttributionHydrator />
									<GlobalLoginModal />
									{/* Customer-support widget floats across every
								    portal page. Mounts inside TenantGate so it
								    has tenant context, and inside PortalAuth's
								    reach so it can read the user's token. */}
									<HelpChatWidget />
									<Routes>
										{/* Client selector — separate page outside PortalLayout */}
										<Route
											path="/select-client"
											element={<ClientSelectorPage />}
										/>

										{/* Self-serve customer registration — public,
									    chrome-less, sits outside PortalLayout because
									    the visitor is by definition unauthenticated.
									    The page itself gates on portal_site
									    isRegistrationEnabled before showing the form. */}
										<Route path="/register" element={<RegisterPage />} />

										{/* Password recovery / new-user flows. Outside
									    PortalLayout so they render without nav chrome
									    (the user usually arrives via an emailed link
									    while signed out). */}
										<Route
											path="/forgot-password"
											element={<ForgotPasswordPage />}
										/>
										<Route
											path="/reset-password"
											element={<SetPasswordPage mode="reset" />}
										/>
										<Route
											path="/set-password"
											element={<SetPasswordPage mode="set-initial" />}
										/>

										{/* All portal routes wrapped in PortalLayout */}
										<Route path="/" element={<PortalLayout />}>
											<Route index element={<HomePage />} />
											<Route path="dashboard" element={<DashboardPage />} />
											<Route
												path="catalog"
												element={
													<RequireCatalogBrowsing>
														<CatalogPage />
													</RequireCatalogBrowsing>
												}
											/>
											<Route
												path="product/:id"
												element={
													<RequireCatalogBrowsing>
														<ProductDetailPage />
													</RequireCatalogBrowsing>
												}
											/>
											<Route
												path="promos/:id"
												element={
													<RequireCatalogBrowsing>
														<PromoDetailPage />
													</RequireCatalogBrowsing>
												}
											/>
											<Route
												path="cart"
												element={
													<RequirePurchasing>
														<CartPage />
													</RequirePurchasing>
												}
											/>
											<Route path="orders" element={<OrdersPage />} />
											<Route path="orders/:id" element={<OrderDetailPage />} />
											{/* Receipt-style line detail — always works (reads the
											    order/invoice, never the catalog), so no route guard;
											    the catalog gate is handled by where it's linked from. */}
											<Route
												path="orders/:id/line/:lineId"
												element={<LineReceiptPage source="order" />}
											/>
											<Route
												path="invoices"
												element={
													<RequireInvoices>
														<InvoicesPage />
													</RequireInvoices>
												}
											/>
											<Route
												path="invoices/:id"
												element={
													<RequireInvoices>
														<InvoiceDetailPage />
													</RequireInvoices>
												}
											/>
											<Route
												path="invoices/:id/line/:lineId"
												element={
													<RequireInvoices>
														<LineReceiptPage source="invoice" />
													</RequireInvoices>
												}
											/>
											<Route
												path="statements"
												element={
													<RequireInvoices>
														<StatementsPage />
													</RequireInvoices>
												}
											/>
											<Route
												path="transactions"
												element={
													<RequireInvoices>
														<TransactionsPage />
													</RequireInvoices>
												}
											/>
											<Route
												path="statements/:id"
												element={
													<RequireInvoices>
														<StatementDetailPage />
													</RequireInvoices>
												}
											/>
											<Route
												path="payments/make"
												element={
													<RequirePayments>
														<MakePaymentPage />
													</RequirePayments>
												}
											/>
											<Route path="account" element={<AccountPage />} />
											<Route
												path="account/payment-methods"
												element={
													<RequirePayments>
														<PaymentMethodsPage />
													</RequirePayments>
												}
											/>
											<Route
												path="account/password"
												element={<ChangePasswordPage />}
											/>

											{/* Catch-all redirect to home */}
											<Route path="*" element={<Navigate to="/" replace />} />
										</Route>
									</Routes>
								</TenantGate>
							</TenantBrandingProvider>
						</OverlayProvider>
					</TranslationProvider>
				</ThemeProvider>
			</BrowserRouter>
		</QueryClientProvider>
	);
}

export default App;
